import { VendorPaymentMethodType, } from '../types/vendor.js';
import { isPositiveAmount, roundMoney } from './money.js';
export const BANK_ACCOUNT_TYPES = [
    { value: 'CHECKING', label: 'Checking' },
    { value: 'SAVINGS', label: 'Savings' },
];
export const VENDOR_PAYMENT_METHOD_SPECS = {
    [VendorPaymentMethodType.ZELLE]: {
        type: VendorPaymentMethodType.ZELLE,
        label: 'Zelle',
        description: 'Sent from the office bank app to the email or phone enrolled with Zelle.',
        oneOf: ['email', 'phone'],
        payableByLink: false,
        fields: [
            {
                key: 'recipientName',
                label: 'Recipient name',
                input: 'text',
                required: true,
                help: 'As Zelle shows it — check it matches before sending.',
            },
            { key: 'email', label: 'Zelle email', input: 'email', placeholder: 'payments@vendor.com' },
            { key: 'phone', label: 'Zelle phone', input: 'tel', placeholder: '(305) 555-0100' },
        ],
    },
    [VendorPaymentMethodType.VENMO]: {
        type: VendorPaymentMethodType.VENMO,
        label: 'Venmo',
        description: 'Paid through a Venmo link, prefilled with the amount.',
        payableByLink: true,
        fields: [
            {
                key: 'username',
                label: 'Venmo username',
                input: 'text',
                required: true,
                placeholder: '@Bella-Catering',
            },
            { key: 'recipientName', label: 'Name on the account', input: 'text' },
            {
                key: 'phoneLast4',
                label: 'Phone, last 4 digits',
                input: 'text',
                placeholder: '0100',
                help: 'Venmo asks for these before a first payment.',
                digitsOnly: true,
            },
        ],
    },
    [VendorPaymentMethodType.WIRE_ACH]: {
        type: VendorPaymentMethodType.WIRE_ACH,
        label: 'Wire / ACH',
        description: 'A bank transfer to the vendor’s account.',
        oneOf: ['routingNumber', 'wireRoutingNumber'],
        payableByLink: false,
        fields: [
            { key: 'accountHolderName', label: 'Account holder', input: 'text', required: true },
            {
                key: 'routingNumber',
                label: 'ACH routing number',
                input: 'text',
                placeholder: '9 digits',
                help: 'For ACH and direct deposit.',
                digitsOnly: true,
            },
            {
                key: 'wireRoutingNumber',
                label: 'Wire routing number',
                input: 'text',
                placeholder: '9 digits',
                help: 'Many banks take wires on a different number from their ACH one.',
                digitsOnly: true,
            },
            {
                key: 'bankName',
                label: 'Bank name',
                input: 'text',
                help: 'Filled in from the routing number.',
            },
            {
                key: 'accountNumber',
                label: 'Account number',
                input: 'text',
                required: true,
                sensitive: true,
                digitsOnly: true,
            },
            {
                key: 'accountType',
                label: 'Account type',
                input: 'select',
                required: true,
                options: BANK_ACCOUNT_TYPES.map((t) => ({ ...t })),
            },
            {
                key: 'swiftCode',
                label: 'SWIFT / BIC',
                input: 'text',
                placeholder: 'International wires only',
            },
            { key: 'bankAddress', label: 'Bank address', input: 'text' },
            { key: 'accountHolderAddress', label: 'Account holder address', input: 'text' },
        ],
    },
    [VendorPaymentMethodType.PAYPAL]: {
        type: VendorPaymentMethodType.PAYPAL,
        label: 'PayPal',
        description: 'Paid through a PayPal.Me link, prefilled with the amount.',
        oneOf: ['paypalMeUsername', 'email'],
        payableByLink: true,
        fields: [
            {
                key: 'paypalMeUsername',
                label: 'PayPal.Me username',
                input: 'text',
                placeholder: 'paypal.me/BellaCatering',
                help: 'Needed for a pay link.',
            },
            { key: 'email', label: 'PayPal email', input: 'email' },
        ],
    },
    [VendorPaymentMethodType.CASH_APP]: {
        type: VendorPaymentMethodType.CASH_APP,
        label: 'Cash App',
        description: 'Paid through a Cash App link, prefilled with the amount.',
        payableByLink: true,
        fields: [
            {
                key: 'cashtag',
                label: '$Cashtag',
                input: 'text',
                required: true,
                placeholder: '$BellaCatering',
            },
            {
                key: 'recipientName',
                label: 'Name on the account',
                input: 'text',
                help: 'Check it matches before sending.',
            },
        ],
    },
    [VendorPaymentMethodType.CHECK]: {
        type: VendorPaymentMethodType.CHECK,
        label: 'Check by mail',
        description: 'A check made out to the vendor and mailed to this address.',
        payableByLink: false,
        fields: [
            {
                key: 'payableTo',
                label: 'Pay to the order of',
                input: 'text',
                required: true,
                placeholder: 'Bella Catering LLC',
            },
            {
                key: 'addressLine1',
                label: 'Mailing address',
                input: 'address',
                required: true,
                placeholder: 'Street address or PO box',
            },
            { key: 'addressLine2', label: 'Address line 2', input: 'text', placeholder: 'Suite, unit' },
            { key: 'city', label: 'City', input: 'text', required: true },
            { key: 'state', label: 'State', input: 'text', help: 'Required for a US address.' },
            { key: 'zipCode', label: 'ZIP code', input: 'text', help: 'Required for a US address.' },
            { key: 'country', label: 'Country', input: 'text', placeholder: 'If not the US' },
        ],
    },
};
/** The types in the order they are offered. */
export const VENDOR_PAYMENT_METHOD_TYPES = [
    VendorPaymentMethodType.ZELLE,
    VendorPaymentMethodType.VENMO,
    VendorPaymentMethodType.CASH_APP,
    VendorPaymentMethodType.PAYPAL,
    VendorPaymentMethodType.WIRE_ACH,
    VendorPaymentMethodType.CHECK,
];
export function paymentMethodTypeLabel(type) {
    return VENDOR_PAYMENT_METHOD_SPECS[type]?.label ?? type;
}
/** "@Bella-Catering", "venmo.com/u/Bella-Catering" or "Bella-Catering" → "Bella-Catering". */
export function normalizeVenmoUsername(value) {
    return value
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^(www\.|account\.)?venmo\.com\/(u\/)?/i, '')
        .replace(/^@/, '')
        .replace(/[/?#].*$/, '');
}
/** "https://paypal.me/Bella", "paypal.com/paypalme/Bella" or "@Bella" → "Bella". */
export function normalizePayPalMeUsername(value) {
    return value
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^(www\.)?(paypal\.me\/|paypal\.com\/paypalme\/)/i, '')
        .replace(/^@/, '')
        .replace(/[/?#].*$/, '');
}
/** "$Bella", "cash.app/$Bella" or "Bella" → "Bella". */
export function normalizeCashtag(value) {
    return value
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^(www\.)?cash\.app\//i, '')
        .replace(/^\$/, '')
        .replace(/[/?#].*$/, '');
}
/** Cashtags: up to 20 letters, digits, hyphens or underscores, at least one a letter. */
export const CASHTAG_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9_-]{1,20}$/;
/** Venmo usernames: 5–30 letters, digits, hyphens or underscores. */
export const VENMO_USERNAME_PATTERN = /^[A-Za-z0-9_-]{5,30}$/;
/** PayPal.Me names: up to 20 letters and digits. */
export const PAYPAL_ME_USERNAME_PATTERN = /^[A-Za-z0-9]{1,20}$/;
/** SWIFT/BIC: 4 bank + 2 country + 2 location letters/digits, optional 3 branch. */
export const SWIFT_CODE_PATTERN = /^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/;
function isRoutingPrefix(prefix) {
    return (prefix <= 12 ||
        (prefix >= 21 && prefix <= 32) ||
        (prefix >= 61 && prefix <= 72) ||
        prefix === 80);
}
/**
 * Whether a US ABA routing number is well formed: nine digits whose weighted
 * sum (3, 7, 1 repeating) is a multiple of ten. Catches a transposed or
 * mistyped digit before money is sent to the wrong bank.
 */
export function isValidAbaRoutingNumber(value) {
    if (!/^\d{9}$/.test(value))
        return false;
    // The first two digits name a Federal Reserve district: 00 (US government),
    // 01–12, 21–32 (thrifts), 61–72 (electronic) or 80 (traveller's cheques).
    // Anything else — all zeros included — is no bank's number.
    if (/^0{9}$/.test(value) || !isRoutingPrefix(Number(value.slice(0, 2))))
        return false;
    const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
    const sum = weights.reduce((acc, w, i) => acc + w * Number(value[i]), 0);
    return sum % 10 === 0;
}
/** "123456789" → "•••• 6789". */
export function maskAccountNumber(value) {
    if (!value)
        return '';
    const tail = value.slice(-4);
    return value.length > 4 ? `•••• ${tail}` : tail;
}
/**
 * A link that opens the service ready to pay this vendor, or null for a
 * method that has no such link (Zelle lives in the bank's own app; a wire is
 * sent from the bank; a check goes in the post).
 */
export function vendorPaymentLink(method, options = {}) {
    const amount = options.amount != null && isPositiveAmount(options.amount)
        ? roundMoney(options.amount).toFixed(2)
        : null;
    const note = options.note?.trim() || null;
    if (method.type === VendorPaymentMethodType.VENMO) {
        const username = normalizeVenmoUsername(method.details.username ?? '');
        if (!username)
            return null;
        const params = new URLSearchParams({ txn: 'pay' });
        if (amount)
            params.set('amount', amount);
        if (note)
            params.set('note', note);
        return `https://venmo.com/${encodeURIComponent(username)}?${params.toString()}`;
    }
    if (method.type === VendorPaymentMethodType.PAYPAL) {
        const username = normalizePayPalMeUsername(method.details.paypalMeUsername ?? '');
        if (!username)
            return null;
        const base = `https://www.paypal.com/paypalme/${encodeURIComponent(username)}`;
        return amount ? `${base}/${amount}USD` : base;
    }
    if (method.type === VendorPaymentMethodType.CASH_APP) {
        const cashtag = normalizeCashtag(method.details.cashtag ?? '');
        if (!cashtag)
            return null;
        const base = `https://cash.app/$${encodeURIComponent(cashtag)}`;
        return amount ? `${base}/${amount}` : base;
    }
    return null;
}
/**
 * The mailing address as it goes on an envelope — "12 Palm Ave, Suite 4",
 * "Miami, FL 33101", and the country when there is one.
 */
export function checkMailingLines(details) {
    const street = [details.addressLine1, details.addressLine2].filter(Boolean).join(', ');
    const place = [details.city, [details.state, details.zipCode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
    return [street, place, details.country ?? ''].filter(Boolean);
}
/** One line naming where the money goes — "@Bella-Catering", "Chase ···· 6789". */
export function summarizePaymentMethod(method) {
    switch (method.type) {
        case VendorPaymentMethodType.ZELLE:
            return [method.details.recipientName, method.details.email || method.details.phone]
                .filter(Boolean)
                .join(' · ');
        case VendorPaymentMethodType.VENMO:
            return `@${normalizeVenmoUsername(method.details.username)}`;
        case VendorPaymentMethodType.PAYPAL:
            return method.details.paypalMeUsername
                ? `paypal.me/${normalizePayPalMeUsername(method.details.paypalMeUsername)}`
                : (method.details.email ?? '');
        case VendorPaymentMethodType.WIRE_ACH:
            return [method.details.bankName, maskAccountNumber(method.details.accountNumber)]
                .filter(Boolean)
                .join(' ');
        case VendorPaymentMethodType.CASH_APP:
            return `$${normalizeCashtag(method.details.cashtag)}`;
        case VendorPaymentMethodType.CHECK:
            return [
                method.details.payableTo,
                [method.details.city, method.details.state].filter(Boolean).join(', '),
            ]
                .filter(Boolean)
                .join(' · ');
        default:
            return '';
    }
}
/**
 * The details as plain lines to paste into a bank's transfer form. Includes
 * the account number whole — only ever built when somebody asks to copy.
 */
export function paymentMethodCopyText(method) {
    if (method.type === VendorPaymentMethodType.CHECK) {
        // An envelope reads as an address block, not as labelled fields.
        return [
            `Pay to the order of: ${method.details.payableTo}`,
            'Mail to:',
            ...checkMailingLines(method.details),
        ].join('\n');
    }
    const spec = VENDOR_PAYMENT_METHOD_SPECS[method.type];
    const details = method.details;
    return spec.fields
        .map((field) => {
        const raw = details[field.key];
        if (raw == null || raw === '')
            return null;
        const value = field.options?.find((o) => o.value === raw)?.label ??
            (typeof raw === 'string' ? raw : String(raw));
        return `${field.label}: ${value}`;
    })
        .filter((line) => line !== null)
        .join('\n');
}
/** A copy of the details with the account number masked — what an audit entry keeps. */
export function maskPaymentDetails(method) {
    const details = { ...method.details };
    if (method.type === VendorPaymentMethodType.WIRE_ACH) {
        const wire = details;
        details.accountNumber = maskAccountNumber(wire.accountNumber);
    }
    return details;
}
//# sourceMappingURL=vendorPaymentMethods.js.map